import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { submitTip } from "@/lib/api";

const MAX_FILES = 8;
const MAX_SIZE = 50 * 1024 * 1024;

const TYPES = [
  { value: "topic_request", label: "TOPIC REQUEST" },
  { value: "tip_off", label: "TIP-OFF / LEAK" },
];

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

export default function Tip() {
  const [params] = useSearchParams();
  const initialType = params.get("type") === "tip_off" ? "tip_off" : "topic_request";
  const [type, setType] = useState(initialType);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [hp, setHp] = useState(""); // honeypot — hidden from real users

  const onFiles = (e) => {
    const incoming = Array.from(e.target.files || []);
    const combined = [...files];
    for (const f of incoming) {
      if (f.size > MAX_SIZE) {
        toast.error(`${f.name} exceeds 50 MB.`);
        continue;
      }
      if (combined.length >= MAX_FILES) {
        toast.error(`Max ${MAX_FILES} files.`);
        break;
      }
      combined.push(f);
    }
    setFiles(combined);
    e.target.value = "";
  };

  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !message.trim()) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("type", type);
      fd.append("email", email);
      fd.append("message", message);
      if (name.trim()) fd.append("name", name.trim());
      fd.append("company", hp);
      files.forEach((f) => fd.append("files", f));
      await submitTip(fd);
      setSent(true);
      toast.success("TRANSMISSION RECEIVED // Your intel is secured.");
      setName(""); setEmail(""); setMessage(""); setFiles([]); setType("topic_request");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transmission failed. Retry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen overflow-hidden bg-void px-6 py-16 text-white md:py-24">
      <div className="pointer-events-none absolute left-1/2 top-0 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-hazard-amber/5 blur-[130px]" />
      <div className="relative mx-auto max-w-2xl">
        <Link to="/" className="font-mono text-xs tracking-widest text-white/50 hover:text-hazard-amber" data-testid="tip-back">
          ← BACK TO SITE
        </Link>

        <div className="mt-6 flex items-center gap-3 font-mono text-[11px] tracking-[0.3em] text-reactor-red md:text-[13px]">
          <span className="hazard-stripes h-3 w-10" />
          ENCRYPTED DROP · SOURCE PROTECTED
        </div>
        <h1 className="mt-4 font-display text-5xl uppercase leading-[0.85] text-white md:text-7xl">
          Submit Intel
        </h1>
        <p className="mt-5 max-w-xl font-body text-sm font-light leading-relaxed text-white/60 md:text-lg">
          Request a topic, or pass us a tip-off. Type your report in detail and attach any supporting
          documents. Leave your email so we can reach you securely if we need more.
        </p>

        <AnimatePresence mode="wait">
          {sent ? (
            <motion.div
              key="sent"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="clip-hud mt-10 border border-tactical-green/40 bg-tactical-green/[0.06] p-8 backdrop-blur-md"
              data-testid="tip-success"
            >
              <div className="flex items-center gap-3 font-mono text-tactical-green">
                <span className="h-2.5 w-2.5 animate-pulse bg-tactical-green" />
                <span className="tracking-[0.2em] md:text-[15px]">&gt;&gt; TRANSMISSION RECEIVED</span>
              </div>
              <p className="mt-3 font-mono text-xs font-light leading-relaxed text-white/70 md:text-[15px]">
                Your intel has been secured in the Vault. If a follow-up is required, we&apos;ll reach out to
                the contact you provided. Stay dark.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button
                  onClick={() => setSent(false)}
                  className="border border-white/25 px-6 py-3 font-mono text-[11px] tracking-[0.25em] text-white/80 transition-colors hover:border-hazard-amber hover:text-hazard-amber"
                  data-testid="tip-another"
                >
                  SEND ANOTHER
                </button>
                <Link
                  to="/"
                  className="border border-reactor-red bg-reactor-red px-6 py-3 font-mono text-[11px] tracking-[0.25em] text-void transition-colors hover:bg-transparent hover:text-reactor-red"
                >
                  RETURN TO BASE
                </Link>
              </div>
            </motion.div>
          ) : (
            <motion.form
              key="form"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              onSubmit={onSubmit}
              className="clip-hud mt-10 border border-hazard-amber/25 bg-bunker-shadow/70 p-6 backdrop-blur-md md:p-8"
              data-testid="tip-form"
            >
              {/* Honeypot: hidden from users, bots tend to fill it */}
              <input
                type="text"
                tabIndex={-1}
                autoComplete="off"
                value={hp}
                onChange={(e) => setHp(e.target.value)}
                className="absolute left-[-9999px] top-[-9999px] h-0 w-0 opacity-0"
                aria-hidden="true"
                name="company"
                data-testid="tip-honeypot"
              />
              {/* Type */}
              <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-hazard-amber md:text-[13px]">
                Transmission Type
              </label>
              <div className="mb-6 grid grid-cols-2 gap-3">
                {TYPES.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    onClick={() => setType(t.value)}
                    className={`border px-4 py-3 font-mono text-[11px] tracking-[0.2em] transition-colors duration-100 md:text-[13px] ${
                      type === t.value
                        ? "border-reactor-red bg-reactor-red/10 text-reactor-red"
                        : "border-white/15 text-white/50 hover:border-white/40"
                    }`}
                    data-testid={`tip-type-${t.value}`}
                  >
                    {t.label}
                  </button>
                ))}
              </div>

              {/* Name */}
              <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-hazard-amber md:text-[13px]">
                Name / Alias <span className="text-white/30">(optional)</span>
              </label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Anonymous"
                className="mb-6 w-full border-b border-white/15 bg-transparent pb-2 font-mono text-white placeholder:text-white/25 focus:border-hazard-amber focus:outline-none md:text-lg"
                data-testid="tip-name"
              />

              {/* Email */}
              <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-hazard-amber md:text-[13px]">
                Contact Email <span className="text-reactor-red">*</span>
              </label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@secure.io"
                className="mb-6 w-full border-b border-white/15 bg-transparent pb-2 font-mono text-white placeholder:text-white/25 focus:border-hazard-amber focus:outline-none md:text-lg"
                data-testid="tip-email"
              />

              {/* Message */}
              <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-hazard-amber md:text-[13px]">
                Your Report <span className="text-reactor-red">*</span>
              </label>
              <textarea
                required
                rows={7}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Lay it all out. The more detail, the better..."
                className="mb-6 w-full resize-y border border-white/15 bg-void/50 p-4 font-mono text-sm leading-relaxed text-white placeholder:text-white/25 focus:border-hazard-amber focus:outline-none md:text-[15px]"
                data-testid="tip-message"
              />

              {/* Files */}
              <label className="mb-2 block font-mono text-[11px] uppercase tracking-[0.25em] text-hazard-amber md:text-[13px]">
                Attachments <span className="text-white/30">(up to {MAX_FILES}, 50 MB each)</span>
              </label>
              <label
                htmlFor="tip-file-input"
                className="flex cursor-pointer items-center justify-center gap-3 border border-dashed border-white/25 bg-void/40 px-4 py-6 font-mono text-[11px] tracking-[0.2em] text-white/50 transition-colors hover:border-hazard-amber hover:text-hazard-amber md:text-[13px]"
                data-testid="tip-file-drop"
              >
                <span className="text-lg leading-none">＋</span> ATTACH DOCUMENTS
              </label>
              <input
                id="tip-file-input"
                type="file"
                multiple
                onChange={onFiles}
                className="hidden"
                data-testid="tip-file-input"
              />

              {files.length > 0 && (
                <ul className="mt-4 space-y-2" data-testid="tip-file-list">
                  {files.map((f, i) => (
                    <li key={i} className="flex items-center justify-between border border-white/10 bg-void/40 px-3 py-2 font-mono text-[11px] text-white/70 md:text-[13px]">
                      <span className="truncate pr-3">{f.name} <span className="text-white/30">· {fmtSize(f.size)}</span></span>
                      <button type="button" onClick={() => removeFile(i)} className="shrink-0 text-reactor-red hover:text-white" aria-label="remove">
                        ✕
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              <button
                type="submit"
                disabled={loading}
                className="mt-8 w-full border border-reactor-red bg-reactor-red px-8 py-4 font-mono text-xs font-bold tracking-[0.35em] text-void transition-colors duration-100 hover:bg-transparent hover:text-reactor-red disabled:opacity-60 md:text-[15px]"
                data-testid="tip-submit"
              >
                {loading ? "TRANSMITTING..." : "► TRANSMIT SECURELY"}
              </button>
              <p className="mt-4 text-center font-mono text-[10px] tracking-widest text-white/40 md:text-[12px]">
                ENCRYPTED · SOURCE PROTECTED
              </p>
            </motion.form>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
